import { useEffect, useState, useRef } from "react";
import { MapContainer, useMap } from "react-leaflet";
import L from "leaflet";
import { getData } from "../../../../../Services/Services";
import { getQueriesListByWidgetTypeForAPI } from "../../../../../Services/EndPoints";

const Map = ({
  item,
  selectedBankIds,
  selectedBranchIds,
  uploadedIcons,
  iconMap,
  iconSizes,
  attachmentList,
  instanceBankIds,
  getThemeURL,
}) => {
  console.log("map item attachmentList", attachmentList);
  console.log("map item", item);
  console.log("mapselectedBankIds", selectedBankIds);
  console.log("mapselectedBranchIds", selectedBranchIds);

  const [widgetIcons, setWidgetIcons] = useState({});
  const blobUrlsRef = useRef(new Set());
  const [mapData, setMapData] = useState(item.data || { points: [] });
  const [loading, setLoading] = useState(false);
  const [savedQueries, setSavedQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [queryError, setQueryError] = useState(null);

  const validateMapPoints = (points) => {
    if (!Array.isArray(points)) {
      console.warn("Map: Points is not an array, returning empty array");
      return [];
    }

    const validPoints = points.filter((point) => {
      const hasValidLat =
        point.lat !== undefined &&
        point.lat !== null &&
        !isNaN(parseFloat(point.lat));
      const hasValidLng =
        point.lng !== undefined &&
        point.lng !== null &&
        !isNaN(parseFloat(point.lng));

      if (!hasValidLat || !hasValidLng) {
        console.warn("Map: Filtering out invalid point:", point);
        return false;
      }

      const lat = parseFloat(point.lat);
      const lng = parseFloat(point.lng);

      const isValidLatRange = lat >= -90 && lat <= 90;
      const isValidLngRange = lng >= -180 && lng <= 180;

      if (!isValidLatRange || !isValidLngRange) {
        console.warn("Map: Filtering out point with invalid coordinates:", {
          lat,
          lng,
        });
        return false;
      }

      return true;
    });

    console.log(`Map: Validated ${validPoints.length}/${points.length} points`);
    return validPoints;
  };

  const getWidgetId = () => {
    return item?.widgetId || item?.id;
  };

  const getWidgetIconKey = (serviceName) => {
    const widgetId = getWidgetId();
    return widgetId ? `widget_${widgetId}_service_${serviceName}` : null;
  };

  const cleanupBlobUrls = (urlsToCleanup = []) => {
    urlsToCleanup.forEach((url) => {
      if (typeof url === "string" && url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
        blobUrlsRef.current.delete(url);
      }
    });
  };

  useEffect(() => {
    const widgetId = getWidgetId();
    if (!widgetId) {
      setWidgetIcons({});
      return;
    }

    cleanupBlobUrls(Object.values(widgetIcons));

    const newWidgetIcons = {};
    const newBlobUrls = new Set();

    if (attachmentList && attachmentList.length > 0) {
      const widgetAttachments = attachmentList.filter(
        (attachment) =>
          attachment.type === "MAP" &&
          attachment.widgetId === widgetId &&
          attachment.serviceName &&
          !attachment._markedForDeletion
      );

      widgetAttachments.forEach((attachment) => {
        const iconKey = getWidgetIconKey(attachment.serviceName);
        if (!iconKey) return;

        if (attachment.mediaPath && typeof attachment.mediaPath === "string") {
          newWidgetIcons[iconKey] = attachment.mediaPath;
        } else if (
          attachment.file &&
          Array.isArray(attachment.file) &&
          attachment.file.length > 0
        ) {
          try {
            const uint8Array = new Uint8Array(attachment.file);
            const blob = new Blob([uint8Array], {
              type: attachment.mimeType || "image/png",
            });
            const blobUrl = URL.createObjectURL(blob);
            newWidgetIcons[iconKey] = blobUrl;
            newBlobUrls.add(blobUrl);
            blobUrlsRef.current.add(blobUrl);
          } catch (error) {
            console.error("Error creating blob URL:", error);
          }
        }
      });
    }

    setWidgetIcons(newWidgetIcons);
  }, [attachmentList, item?.widgetId, item?.id]);

  useEffect(() => {
    // Only fetch saved queries when filters are actually applied AND dataSource exists
    if (
      item?.dataSource &&
      savedQueries.length === 0 &&
      ((selectedBankIds && selectedBankIds.length > 0) ||
        (selectedBranchIds && selectedBranchIds.length > 0))
    ) {
      const fetchSavedQueries = async () => {
        try {
          setLoadingQueries(true);
          setQueryError(null);
          console.log(
            "Map: Fetching saved queries for MAP type (with filters applied)"
          );

          const response = await getData(
            getQueriesListByWidgetTypeForAPI.replace("WIDGETTYPE", "MAP")
          );

          console.log("Map: Raw saved queries response:", response);

          const parsed = response.map((queryItem) => ({
            ...queryItem,
            queryBuilder:
              typeof queryItem.queryBuilder === "string"
                ? JSON.parse(queryItem.queryBuilder)
                : queryItem.queryBuilder,
          }));

          console.log("Map: Parsed saved queries:", parsed);
          setSavedQueries(parsed);
        } catch (err) {
          console.error("Map: Error fetching saved queries:", err);
          setQueryError("Failed to load saved queries");
        } finally {
          setLoadingQueries(false);
        }
      };

      fetchSavedQueries();
    }
  }, [
    item?.dataSource,
    savedQueries.length,
    selectedBankIds,
    selectedBranchIds,
  ]);

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []);

  // Transform NiFi response - UPDATED to match Card component
  const transformNiFiDataToMapFormat = (nifiResponse) => {
    console.log("Map: Raw NiFi response:", nifiResponse);

    try {
      // PRIORITY 1: Handle aggregatedData structure (matching Card component)
      if (nifiResponse && nifiResponse.aggregatedData) {
        console.log(
          "Map: Processing aggregatedData:",
          nifiResponse.aggregatedData
        );

        const { aggregatedData } = nifiResponse;

        if (
          aggregatedData.status === "success" &&
          aggregatedData.data &&
          Array.isArray(aggregatedData.data)
        ) {
          console.log("Map: Aggregated data validation passed");

          // If data is in object format
          if (
            aggregatedData.data.length > 0 &&
            typeof aggregatedData.data[0] === "object"
          ) {
            console.log("Map: Aggregated data is in object format");

            const points = [];

            // Get the data source type from item.dataSource
            const defaultType = Array.isArray(item.dataSource)
              ? item.dataSource[0]
              : item.dataSource || "default";

            aggregatedData.data.forEach((dataItem, index) => {
              const lat = parseFloat(
                dataItem.lat ||
                  dataItem.latitude ||
                  dataItem.Latitude ||
                  dataItem.LAT ||
                  dataItem.y ||
                  dataItem.Y
              );
              const lng = parseFloat(
                dataItem.lng ||
                  dataItem.longitude ||
                  dataItem.Longitude ||
                  dataItem.LNG ||
                  dataItem.x ||
                  dataItem.X
              );

              if (
                !isNaN(lat) &&
                !isNaN(lng) &&
                lat >= -90 &&
                lat <= 90 &&
                lng >= -180 &&
                lng <= 180
              ) {
                // Use dataItem's type field, or fall back to the dataSource from item config
                const pointData = {
                  lat: lat,
                  lng: lng,
                  type:
                    dataItem.type ||
                    dataItem.source ||
                    dataItem.category ||
                    defaultType,
                };

                Object.keys(dataItem).forEach((key) => {
                  const lowerKey = key.toLowerCase();
                  if (
                    ![
                      "lat",
                      "lng",
                      "latitude",
                      "longitude",
                      "x",
                      "y",
                      "type",
                      "source",
                      "category",
                    ].includes(lowerKey)
                  ) {
                    pointData[key] = dataItem[key];
                  }
                });

                points.push(pointData);
                console.log(
                  `Map: Created point with type "${pointData.type}":`,
                  pointData
                );
              }
            });

            const validatedPoints = validateMapPoints(points);
            console.log(
              `Map: Returning ${validatedPoints.length} points with types:`,
              [...new Set(validatedPoints.map((p) => p.type))]
            );
            return { points: validatedPoints };
          }

          // If data is array of arrays with columns
          if (
            aggregatedData.columns &&
            Array.isArray(aggregatedData.columns) &&
            aggregatedData.data.length > 0 &&
            Array.isArray(aggregatedData.data[0])
          ) {
            console.log("Map: Transforming aggregated data from array format");

            // Get the data source type from item.dataSource
            const defaultType = Array.isArray(item.dataSource)
              ? item.dataSource[0]
              : item.dataSource || "default";

            const transformedData = aggregatedData.data.map((row) => {
              const dataPoint = {};
              aggregatedData.columns.forEach((column, index) => {
                let cleanKey = column
                  .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, "")
                  .replace(/\)$/, "")
                  .replace(/^.*\./, "");
                dataPoint[cleanKey] = row[index];
              });
              return dataPoint;
            });

            const points = transformedData.map((dataItem) => {
              const lat = parseFloat(
                dataItem.lat ||
                  dataItem.latitude ||
                  dataItem.Latitude ||
                  25.5941
              );
              const lng = parseFloat(
                dataItem.lng ||
                  dataItem.longitude ||
                  dataItem.Longitude ||
                  85.1376
              );
              // Use dataItem's type field, or fall back to the dataSource from item config
              const type = dataItem.type || dataItem.source || defaultType;

              const pointData = { lat, lng, type };

              Object.keys(dataItem).forEach((key) => {
                const lowerKey = key.toLowerCase();
                if (
                  ![
                    "lat",
                    "lng",
                    "latitude",
                    "longitude",
                    "type",
                    "source",
                  ].includes(lowerKey)
                ) {
                  pointData[key] = dataItem[key];
                }
              });

              return pointData;
            });

            const validatedPoints = validateMapPoints(points);
            console.log(
              `Map: Returning ${validatedPoints.length} points from array format with types:`,
              [...new Set(validatedPoints.map((p) => p.type))]
            );
            return { points: validatedPoints };
          }
        } else if (aggregatedData.recordCount === 0) {
          console.log("Map: Aggregated data has no records");
          return { points: [] };
        }
      }

      // PRIORITY 2: Handle multi-bank response structure
      if (
        nifiResponse &&
        nifiResponse.responses &&
        Array.isArray(nifiResponse.responses)
      ) {
        console.log("Map: Processing multi-bank responses");
        const allPoints = [];

        nifiResponse.responses.forEach((bankResponse) => {
          console.log(`Map: Processing bank ${bankResponse.bankId} response`);

          if (bankResponse.status === "success" && bankResponse.data) {
            const bankData = bankResponse.data;

            if (
              bankData.status === "success" &&
              bankData.data &&
              Array.isArray(bankData.data)
            ) {
              if (
                bankData.data.length > 0 &&
                typeof bankData.data[0] === "object"
              ) {
                bankData.data.forEach((dataItem) => {
                  const lat = parseFloat(
                    dataItem.lat || dataItem.latitude || dataItem.Latitude
                  );
                  const lng = parseFloat(
                    dataItem.lng || dataItem.longitude || dataItem.Longitude
                  );

                  if (
                    !isNaN(lat) &&
                    !isNaN(lng) &&
                    lat >= -90 &&
                    lat <= 90 &&
                    lng >= -180 &&
                    lng <= 180
                  ) {
                    const pointData = {
                      lat,
                      lng,
                      type: dataItem.type || dataItem.source || "default",
                    };

                    Object.keys(dataItem).forEach((key) => {
                      const lowerKey = key.toLowerCase();
                      if (
                        ![
                          "lat",
                          "lng",
                          "latitude",
                          "longitude",
                          "type",
                          "source",
                        ].includes(lowerKey)
                      ) {
                        pointData[key] = dataItem[key];
                      }
                    });

                    if (!pointData.bank_id && !pointData.bankId) {
                      pointData.bank_id = bankResponse.bankId;
                    }

                    allPoints.push(pointData);
                  }
                });
              }
            }
          }
        });

        const validatedPoints = validateMapPoints(allPoints);
        return { points: validatedPoints };
      }

      // PRIORITY 3: Handle single response format
      if (
        nifiResponse &&
        nifiResponse.status === "success" &&
        nifiResponse.data
      ) {
        console.log("Map: Processing single response format");
        const { data } = nifiResponse;

        if (
          Array.isArray(data) &&
          data.length > 0 &&
          typeof data[0] === "object"
        ) {
          const points = data.map((dataItem) => {
            const lat = parseFloat(
              dataItem.lat || dataItem.latitude || dataItem.Latitude || 25.5941
            );
            const lng = parseFloat(
              dataItem.lng ||
                dataItem.longitude ||
                dataItem.Longitude ||
                85.1376
            );
            const type = dataItem.type || dataItem.source || "default";

            const pointData = { lat, lng, type };

            Object.keys(dataItem).forEach((key) => {
              const lowerKey = key.toLowerCase();
              if (
                ![
                  "lat",
                  "lng",
                  "latitude",
                  "longitude",
                  "type",
                  "source",
                ].includes(lowerKey)
              ) {
                pointData[key] = dataItem[key];
              }
            });

            return pointData;
          });

          const validatedPoints = validateMapPoints(points);
          return { points: validatedPoints };
        }

        if (
          nifiResponse.columns &&
          Array.isArray(data) &&
          Array.isArray(data[0])
        ) {
          const transformedData = data.map((row) => {
            const dataPoint = {};
            nifiResponse.columns.forEach((column, index) => {
              let cleanKey = column
                .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, "")
                .replace(/\)$/, "")
                .replace(/^.*\./, "");
              dataPoint[cleanKey] = row[index];
            });
            return dataPoint;
          });

          const points = transformedData.map((dataItem) => {
            const lat = parseFloat(
              dataItem.lat || dataItem.latitude || 25.5941
            );
            const lng = parseFloat(
              dataItem.lng || dataItem.longitude || 85.1376
            );
            const type = dataItem.type || dataItem.source || "default";

            const pointData = { lat, lng, type };

            Object.keys(dataItem).forEach((key) => {
              const lowerKey = key.toLowerCase();
              if (
                ![
                  "lat",
                  "lng",
                  "latitude",
                  "longitude",
                  "type",
                  "source",
                ].includes(lowerKey)
              ) {
                pointData[key] = dataItem[key];
              }
            });

            return pointData;
          });

          const validatedPoints = validateMapPoints(points);
          return { points: validatedPoints };
        }
      }

      console.log("Map: No valid data structure found");
      return { points: [] };
    } catch (error) {
      console.error("Map: Error transforming NiFi data:", error);
      return { points: [] };
    }
  };

  const isBankOnlyQuery = (queryObject) => {
    const entity = queryObject.entity || "";
    const joins = queryObject.joins || [];

    const isCoreBank =
      entity.toLowerCase().includes("core.bank") ||
      entity.toLowerCase() === "bank";
    const hasNoJoins = joins.length === 0;

    console.log("Map: Checking if bank-only query:", {
      entity,
      isCoreBank,
      hasNoJoins,
      joins,
    });

    return isCoreBank && hasNoJoins;
  };

  // Fetch filtered data - UPDATED to match Card component's approach
  const fetchFilteredData = async (bankIds, branchIds) => {
    try {
      if (!getData || typeof getData !== "function") {
        console.log("Map: getData function not available");
        const originalData = item.data || { points: [] };
        const validatedPoints = validateMapPoints(originalData.points);
        return { ...originalData, points: validatedPoints };
      }

      if (!getThemeURL) {
        console.log("Map: getThemeURL not available");
        const originalData = item.data || { points: [] };
        const validatedPoints = validateMapPoints(originalData.points);
        return { ...originalData, points: validatedPoints };
      }

      if (!item?.dataSource || !savedQueries || savedQueries.length === 0) {
        console.log("Map: No data source or saved queries available");
        const originalData = item.data || { points: [] };
        const validatedPoints = validateMapPoints(originalData.points);
        return { ...originalData, points: validatedPoints };
      }

      const currentDashboardWidgetId = `WIDGET_${item.id}`;
      const matchedQuery = savedQueries.find(
        (q) => q.dashboardWidgetId === currentDashboardWidgetId
      );

      console.log("Map matchedQuery", matchedQuery);
      console.log("Map dataSource", item.dataSource);

      if (!matchedQuery) {
        console.log("Map: No matching query found");
        const originalData = item.data || { points: [] };
        const validatedPoints = validateMapPoints(originalData.points);
        return { ...originalData, points: validatedPoints };
      }

      const queryObject =
        typeof matchedQuery.queryBuilder === "string"
          ? JSON.parse(matchedQuery.queryBuilder)
          : matchedQuery.queryBuilder;

      const isBankOnly = isBankOnlyQuery(queryObject);
      console.log("Map: Is bank-only query:", isBankOnly);

      if (isBankOnly) {
        const hasBankIds = bankIds && bankIds.length > 0;
        const hasBranchIds = branchIds && branchIds.length > 0;

        console.log("Map: Bank-only query scenarios:", {
          hasBankIds,
          hasBranchIds,
          bankIds,
          branchIds,
        });

        if (!hasBankIds && hasBranchIds) {
          console.log("Map: Scenario 2 - Only branch IDs for bank-only query");
          return { points: [] };
        }

        if (hasBankIds) {
          console.log(
            `Map: Scenario ${hasBranchIds ? "3" : "1"} - Using bank IDs`
          );

          const filters = {
            "bank.id": {
              operator: "IN",
              value: bankIds,
            },
          };

          const enhancedQuery = {
            ...queryObject,
            filters: {
              ...(queryObject.filters || {}),
              ...filters,
            },
          };

          console.log(
            "Map: Enhanced bank-only query:",
            JSON.stringify(enhancedQuery, null, 2)
          );

          let userResponse;
          try {
            userResponse = await getData(getThemeURL);
          } catch (error) {
            console.error("Map: Error fetching user profile:", error);
            return { points: [] };
          }

          if (
            !userResponse ||
            !Array.isArray(userResponse) ||
            userResponse.length === 0
          ) {
            console.error("Map: Failed to fetch user profile");
            return { points: [] };
          }

          const user = userResponse[0];
          const roleName = user.role_name;
          const userBankId = user.bank_id;

          const authObject = {
            instanceUrl: "/query-builder-api/execute",
            apiType: "POST",
          };

          const processBankIds = (ids) => {
            if (!ids) return [];

            if (Array.isArray(ids)) {
              return ids.map((id) => Number(id));
            } else if (typeof ids === "string") {
              return ids.split(",").map((id) => Number(id.trim()));
            } else {
              return [Number(ids)];
            }
          };

          if (roleName === "SUPER ADMIN") {
            authObject.type = "SA";
            authObject.bank_id = processBankIds(instanceBankIds);
          } else if (user.is_admin === true) {
            authObject.type = "HO";
            authObject.bank_id = processBankIds(userBankId);
          } else {
            authObject.type = "U";
            authObject.bank_id = processBankIds(userBankId);
          }

          const payloadWithAuth = {
            auth: authObject,
            main: enhancedQuery,
          };

          const response = await fetch("/nifi/fetchQuery-API", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payloadWithAuth),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          console.log("Map: Bank-only query result:", result);

          if (result.status === "error") {
            throw new Error(result.message || "Query execution failed");
          }

          const transformedData = transformNiFiDataToMapFormat(result);
          console.log("Map: Transformed bank-only data:", transformedData);
          return transformedData;
        }

        console.log("Map: No bank IDs for bank-only query");
        return { points: [] };
      }

      // NON-BANK-ONLY QUERIES
      else {
        console.log("Map: Processing non-bank-only query");

        const entity = queryObject.entity || "";
        const tableName = entity.includes(".") ? entity.split(".")[1] : entity;

        const joins = queryObject.joins || [];
        const joinedTables = joins.map((join) => {
          const table = join.table || join.alias || "";
          return table.includes(".") ? table.split(".")[1] : table;
        });

        const determineFilterFields = () => {
          let bankFilterField = null;
          let branchFilterField = null;

          console.log("Map: Table analysis:", { tableName, joinedTables });

          if (tableName !== "bank" && tableName !== "branch_details") {
            bankFilterField = `${tableName}.bank_id`;
            branchFilterField = `${tableName}.branch_id`;
            return { bankFilterField, branchFilterField };
          }

          const hasBankTable =
            tableName === "bank" || joinedTables.includes("bank");
          const hasBranchDetailsTable =
            tableName === "branch_details" ||
            joinedTables.includes("branch_details");
          const hasJoins = joins.length > 0;

          if (tableName === "bank") {
            bankFilterField = "bank.id";
            if (!hasJoins) {
              branchFilterField = null;
              return { bankFilterField, branchFilterField };
            }
          } else if (hasBankTable && hasJoins) {
            bankFilterField = "bank.id";
          } else if (
            !hasBankTable &&
            tableName !== "bank" &&
            tableName !== "branch_details"
          ) {
            bankFilterField = `${tableName}.bank_id`;
          } else if (!hasBankTable) {
            bankFilterField = `${tableName}.bank_id`;
          }

          if (tableName === "branch_details") {
            branchFilterField = "branch_details.id";
          } else if (hasBranchDetailsTable && hasJoins) {
            branchFilterField = "branch_details.id";
          } else if (
            !hasBranchDetailsTable &&
            tableName !== "bank" &&
            tableName !== "branch_details"
          ) {
            branchFilterField = `${tableName}.branch_id`;
          } else {
            const tableWithBranchId = joinedTables.find(
              (table) => table !== "bank" && table !== "branch_details"
            );
            if (tableWithBranchId) {
              branchFilterField = `${tableWithBranchId}.branch_id`;
            } else {
              branchFilterField = `${tableName}.branch_id`;
            }
          }

          return { bankFilterField, branchFilterField };
        };

        const { bankFilterField, branchFilterField } = determineFilterFields();

        console.log("Map: Filter fields:", {
          bankFilterField,
          branchFilterField,
        });

        const filters = {};

        if (bankIds && bankIds.length > 0 && bankFilterField) {
          filters[bankFilterField] = {
            operator: "IN",
            value: bankIds,
          };
        }

        if (branchIds && branchIds.length > 0 && branchFilterField) {
          filters[branchFilterField] = {
            operator: "IN",
            value: branchIds,
          };
        } else if (!branchIds || branchIds.length === 0) {
          if (branchFilterField) {
            filters[branchFilterField] = null;
          }
        }

        const enhancedQuery = {
          ...queryObject,
          filters: {
            ...(queryObject.filters || {}),
            ...filters,
          },
        };

        console.log(
          "Map: Enhanced non-bank-only query:",
          JSON.stringify(enhancedQuery, null, 2)
        );

        let userResponse;
        try {
          userResponse = await getData(getThemeURL);
        } catch (error) {
          console.error("Map: Error fetching user profile:", error);
          return { points: [] };
        }

        if (
          !userResponse ||
          !Array.isArray(userResponse) ||
          userResponse.length === 0
        ) {
          console.error("Map: Failed to fetch user profile");
          return { points: [] };
        }

        const user = userResponse[0];
        const roleName = user.role_name;
        const userBankId = user.bank_id;

        const authObject = {
          instanceUrl: "/query-builder-api/execute",
          apiType: "POST",
        };

        const processBankIds = (ids) => {
          if (!ids) return [];

          if (Array.isArray(ids)) {
            return ids.map((id) => Number(id));
          } else if (typeof ids === "string") {
            return ids.split(",").map((id) => Number(id.trim()));
          } else {
            return [Number(ids)];
          }
        };

        if (roleName === "SUPER ADMIN") {
          authObject.type = "SA";
          authObject.bank_id = processBankIds(instanceBankIds);
        } else if (user.is_admin === true) {
          authObject.type = "HO";
          authObject.bank_id = processBankIds(userBankId);
        } else {
          authObject.type = "U";
          authObject.bank_id = processBankIds(userBankId);
        }

        const payloadWithAuth = {
          auth: authObject,
          main: enhancedQuery,
        };

        const response = await fetch("/nifi/fetchQuery-API", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payloadWithAuth),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log("Map: Non-bank-only query result:", result);

        if (result.status === "error") {
          throw new Error(result.message || "Query execution failed");
        }

        const transformedData = transformNiFiDataToMapFormat(result);
        console.log("Map: Transformed non-bank-only data:", transformedData);
        return transformedData;
      }
    } catch (error) {
      console.error("Map: Error fetching filtered data:", error);
      const originalData = item.data || { points: [] };
      const validatedPoints = validateMapPoints(originalData.points);
      return { ...originalData, points: validatedPoints };
    }
  };

  useEffect(() => {
    const loadData = async () => {
      console.log("Map: LoadData called with:", {
        selectedBankIds,
        selectedBranchIds,
        hasDataSource: !!item?.dataSource,
        savedQueriesLength: savedQueries.length,
        loadingQueries,
        itemId: item.id,
      });

      // Always attempt to fetch from NiFi if dataSource and savedQueries are available
      if (
        item?.dataSource &&
        savedQueries &&
        savedQueries.length > 0 &&
        getData &&
        getThemeURL
      ) {
        setLoading(true);
        try {
          console.log("Map: Attempting to fetch data from NiFi");

          const fetchedData = await fetchFilteredData(
            selectedBankIds || [],
            selectedBranchIds || []
          );

          console.log("Map: Setting map data to:", fetchedData);
          setMapData({ ...fetchedData });
        } catch (error) {
          console.error("Map: Error loading filtered data:", error);
          const originalData = item.data || { points: [] };
          const validatedPoints = validateMapPoints(originalData.points);
          setMapData({ ...originalData, points: validatedPoints });
        } finally {
          setLoading(false);
        }
      } else {
        console.log(
          "Map: No NiFi dataSource or required functions, using original data"
        );
        const originalData = item.data || { points: [] };
        const validatedPoints = validateMapPoints(originalData.points);
        setMapData({ ...originalData, points: validatedPoints });
      }
    };

    if (!loadingQueries) {
      loadData();
    }
  }, [
    selectedBankIds,
    selectedBranchIds,
    item.dataSource,
    loadingQueries,
    savedQueries.length,
    item.id,
  ]);

  const filteredItem = {
    ...item,
    data: mapData,
  };

  console.log("Map: Final filteredItem data:", filteredItem.data);

  const baseLayers = {
    Standard: L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      { attribution: "" }
    ),
    Satellite: L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
    ),
    Terrain: L.tileLayer("https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"),
    Hybrid: L.tileLayer("https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"),
    Streets: L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
      {
        maxNativeZoom: 17,
      }
    ),
    Road: L.tileLayer("https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"),
  };

  const getDefaultIcon = () => {
    return (
      iconMap?.default?.options?.iconUrl ||
      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjM0I4MkY2Ci8+PC9zdmc+"
    );
  };

  const getIconForSource = (sourceName) => {
    const size = iconSizes?.[sourceName] || [32, 32];
    const iconKey = getWidgetIconKey(sourceName);

    let iconUrl = null;

    if (iconKey && widgetIcons[iconKey]) {
      iconUrl = widgetIcons[iconKey];
    } else if (iconKey && uploadedIcons && uploadedIcons[iconKey]) {
      iconUrl = uploadedIcons[iconKey];
    } else if (
      iconMap &&
      iconMap[sourceName] &&
      iconMap[sourceName].options &&
      iconMap[sourceName].options.iconUrl
    ) {
      iconUrl = iconMap[sourceName].options.iconUrl;
    } else {
      iconUrl = getDefaultIcon();
    }

    return new L.Icon({
      iconUrl: iconUrl,
      iconSize: size,
      iconAnchor: [size[0] / 2, size[1]],
      popupAnchor: [0, -size[1]],
    });
  };

  const getIconUrlForControl = (sourceName) => {
    const iconKey = getWidgetIconKey(sourceName);

    if (iconKey && widgetIcons[iconKey]) {
      return widgetIcons[iconKey];
    }

    if (iconKey && uploadedIcons && uploadedIcons[iconKey]) {
      return uploadedIcons[iconKey];
    }

    if (
      iconMap &&
      iconMap[sourceName] &&
      iconMap[sourceName].options &&
      iconMap[sourceName].options.iconUrl
    ) {
      return iconMap[sourceName].options.iconUrl;
    }

    return getDefaultIcon();
  };

  if (loading || loadingQueries) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "14px",
        }}
      >
        {loadingQueries ? "Loading queries..." : "Loading map data..."}
      </div>
    );
  }

  if (queryError) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#ef4444",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #ef4444",
          }}
        >
          <div style={{ fontWeight: "600", marginBottom: "4px" }}>
            Error Loading Queries
          </div>
          <div style={{ fontSize: "12px" }}>{queryError}</div>
        </div>
      </div>
    );
  }

  if (!filteredItem.data.points || filteredItem.data.points.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "12px",
          textAlign: "center",
          padding: "20px",
          flexDirection: "column",
          gap: "12px",
          minHeight: "400px",
        }}
      >
        <div
          style={{
            padding: "10px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #f59e0b",
          }}
        >
          <div
            style={{
              color: "#92400e",
              fontWeight: "600",
            }}
          >
            No Data Available
          </div>
        </div>
      </div>
    );
  }

  const MapMarkers = () => {
    const map = useMap();

    useEffect(() => {
      if (!map) return;

      console.log("Map: MapMarkers effect triggered");
      console.log("Map: filteredItem.data:", filteredItem.data);
      console.log("Map: filteredItem.dataSource:", filteredItem.dataSource);

      const handleResize = () => {
        if (map && map.getContainer() && map._mapPane) {
          try {
            map.invalidateSize();
          } catch (error) {
            console.warn("Map invalidateSize failed:", error);
          }
        }
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      window.leafletMap = map;

      map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          map.removeLayer(layer);
        }
      });

      baseLayers.Standard.addTo(map);

      const overlayLayers = {};
      const selectedSources = Array.isArray(filteredItem.dataSource)
        ? filteredItem.dataSource
        : filteredItem.dataSource
        ? [filteredItem.dataSource]
        : [];

      console.log("Map: Selected sources for rendering:", selectedSources);
      console.log(
        "Map: Total points available:",
        filteredItem.data.points?.length || 0
      );

      const pointsBySource = {};
      if (filteredItem.data.points) {
        filteredItem.data.points.forEach((point) => {
          const source = point.type || "unknown";
          if (!pointsBySource[source]) {
            pointsBySource[source] = [];
          }
          pointsBySource[source].push(point);
        });
      }

      console.log("Map: Points grouped by source:", pointsBySource);

      selectedSources.forEach((source) => {
        console.log(`Map: Creating layer for source: ${source}`);

        const group = L.layerGroup();
        const sourcePoints = filteredItem.data.points.filter(
          (point) => point.type === source
        );

        console.log(
          `Map: Found ${sourcePoints.length} points for source "${source}":`,
          sourcePoints
        );

        sourcePoints.forEach((point, index) => {
          const lat = parseFloat(point.lat);
          const lng = parseFloat(point.lng);

          console.log(
            `Map: Processing point ${index} for source "${source}":`,
            { lat, lng, point }
          );

          if (
            isNaN(lat) ||
            isNaN(lng) ||
            lat < -90 ||
            lat > 90 ||
            lng < -180 ||
            lng > 180
          ) {
            console.warn(
              `Map: Skipping marker ${index} for source "${source}" with invalid coordinates:`,
              { lat, lng, point }
            );
            return;
          }

          const fieldsConfig = filteredItem.fieldsBySource?.[source] || [];
          const excludedFields = [
            "bank_id",
            "bankId",
            "Bank_ID",
            "branch_id",
            "branchId",
            "Branch_ID",
            "branchdetails_id",
          ];

          const fieldsToDisplay = fieldsConfig
            .filter((field) => field.selected)
            .filter((field) => !excludedFields.includes(field.key))
            .sort((a, b) => (a.order || 0) - (b.order || 0));

          console.log(
            `Map: Fields to display for source "${source}":`,
            fieldsToDisplay
          );

          const currentIcon = getIconForSource(source);
          const iconUrlForPopup = getIconUrlForControl(source);

          const popupContent = `
  <div style="
    max-height: 180px; overflow-y: auto; padding: 10px; border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); background: white;
    width: 220px; font-family: sans-serif;
  ">
    <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
      <img src="${iconUrlForPopup}" width="20" height="20" style="object-fit: contain;" 
           onerror="this.src='${getDefaultIcon()}'" />
      <span style="font-weight: 700; font-size: 16px; color: #1a7a73;">${source}</span>
    </div>
    <div style="display: grid; grid-template-columns: auto 1fr; gap: 6px; font-size: 14px;">
      ${fieldsToDisplay
        .map((field) => {
          let value = point[field.key];

          if (["lat", "latitude", "Latitude"].includes(field.key)) {
            value = point.lat || point.latitude || point.Latitude || "N/A";
          } else if (["lng", "longitude", "Longitude"].includes(field.key)) {
            value = point.lng || point.longitude || point.Longitude || "N/A";
          }

          return `
            <div style="font-weight: 600; color: #4b5563;">${
              field.label || field.key
            }:</div>
            <div style="color: #1f2937;">${value || "N/A"}</div>
          `;
        })
        .join("")}
    </div>
    <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
      Coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)}
    </div>
  </div>
`;

          const marker = L.marker([lat, lng], {
            icon: currentIcon,
          }).bindPopup(popupContent);

          marker.addTo(group);
          console.log(
            `Map: Added marker ${index} for source "${source}" at [${lat}, ${lng}]`
          );
        });

        overlayLayers[source] = group;
        console.log(
          `Map: Created overlay layer for source "${source}" with ${sourcePoints.length} markers`
        );
      });

      console.log(
        "Map: All overlay layers created:",
        Object.keys(overlayLayers)
      );

      const control = L.control
        .layers(baseLayers, overlayLayers, {
          position: "topright",
          collapsed: false,
        })
        .addTo(map);

      Object.entries(overlayLayers).forEach(([sourceName, layer]) => {
        layer.addTo(map);
        console.log(`Map: Added overlay layer "${sourceName}" to map`);
      });

      const widgetId = getWidgetId();
      const baseLayerStorageKey = `selectedBaseLayer_widget_${widgetId}`;

      map.on("baselayerchange", function (e) {
        localStorage.setItem(baseLayerStorageKey, e.name);
      });

      const savedBaseLayer = localStorage.getItem(baseLayerStorageKey);
      if (savedBaseLayer && baseLayers[savedBaseLayer]) {
        map.eachLayer((layer) => {
          if (layer instanceof L.TileLayer) {
            map.removeLayer(layer);
          }
        });
        baseLayers[savedBaseLayer].addTo(map);
      }

      const geocoderControl = L.Control.geocoder({
        defaultMarkGeocode: false,
        position: "topleft",
      })
        .on("markgeocode", function (e) {
          const { center, html } = e.geocode;

          const searchMarker = L.marker(center).addTo(map);

          const removeMarker = () => {
            map.removeLayer(searchMarker);
          };

          const popupContent = document.createElement("div");
          popupContent.style.cssText = `
            padding: 10px; border-radius: 8px;
            background: white; width: 200px; font-family: sans-serif;
          `;

          popupContent.innerHTML = `
            <div style="margin-bottom: 10px;">
              <div style="font-weight: 600; color: #1a7a73; margin-bottom: 5px;">📍 Searched Location</div>
              <div style="font-size: 14px; color: #1f2937;">${html}</div>
            </div>
            <button 
              id="removeBtn"
              style="
                background: #ef4444; color: white; border: none; padding: 6px 12px;
                border-radius: 4px; cursor: pointer; font-size: 12px;
                width: 100%; transition: background 0.2s;
              "
            >
               Remove Location
            </button>
          `;

          const removeBtn = popupContent.querySelector("#removeBtn");
          removeBtn.addEventListener("click", removeMarker);
          removeBtn.addEventListener("mouseover", function () {
            this.style.background = "#dc2626";
          });
          removeBtn.addEventListener("mouseout", function () {
            this.style.background = "#ef4444";
          });

          searchMarker.bindPopup(popupContent);
          map.setView(center, 14);
          searchMarker.openPopup();
        })
        .addTo(map);

      setTimeout(() => {
        const geocoderEl = document.querySelector(".leaflet-control-geocoder");
        if (geocoderEl) geocoderEl.style.marginTop = "10px";
      }, 0);

      const insertControlIcons = () => {
        const currentWidgetId = getWidgetId();
        const mapContainer = map.getContainer();
        const labels = mapContainer.querySelectorAll(
          ".leaflet-control-layers-overlays label"
        );

        labels.forEach((label) => {
          const source = label.textContent.trim();
          if (label.querySelector("img")) {
            return;
          }

          const currentIconKey = currentWidgetId
            ? `widget_${currentWidgetId}_service_${source}`
            : null;
          let iconUrl = null;

          if (currentIconKey && widgetIcons[currentIconKey]) {
            iconUrl = widgetIcons[currentIconKey];
          } else if (
            currentIconKey &&
            uploadedIcons &&
            uploadedIcons[currentIconKey]
          ) {
            iconUrl = uploadedIcons[currentIconKey];
          } else if (
            iconMap &&
            iconMap[source] &&
            iconMap[source].options &&
            iconMap[source].options.iconUrl
          ) {
            iconUrl = iconMap[source].options.iconUrl;
          } else {
            iconUrl = getDefaultIcon();
          }

          if (iconUrl) {
            const img = document.createElement("img");
            img.src = iconUrl;
            img.style.width = "18px";
            img.style.height = "18px";
            img.style.marginRight = "10px";
            img.style.verticalAlign = "middle";
            img.style.objectFit = "contain";

            img.onerror = function () {
              this.src = getDefaultIcon();
            };

            label.insertBefore(img, label.firstChild.nextSibling);
          }
        });
      };

      const observeControlChanges = () => {
        const mapContainer = map.getContainer();
        const controlContainer = mapContainer.querySelector(
          ".leaflet-control-layers"
        );

        if (controlContainer) {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === "childList") {
                setTimeout(() => insertControlIcons(), 50);
              }
            });
          });

          observer.observe(controlContainer, {
            childList: true,
            subtree: true,
          });

          map._iconObserver = observer;
        }
      };

      setTimeout(insertControlIcons, 100);
      setTimeout(insertControlIcons, 500);
      setTimeout(insertControlIcons, 1000);
      setTimeout(insertControlIcons, 1500);
      setTimeout(observeControlChanges, 200);

      if (filteredItem.data.points?.length > 0) {
        try {
          const validCoords = filteredItem.data.points
            .map((p) => {
              const lat = parseFloat(p.lat);
              const lng = parseFloat(p.lng);

              if (
                isNaN(lat) ||
                isNaN(lng) ||
                lat < -90 ||
                lat > 90 ||
                lng < -180 ||
                lng > 180
              ) {
                return null;
              }

              return [lat, lng];
            })
            .filter((coord) => coord !== null);

          if (validCoords.length > 0) {
            const bounds = L.latLngBounds(validCoords);
            map.fitBounds(bounds, { padding: [50, 50] });
          } else {
            map.setView([25.5941, 85.1376], 13);
          }
        } catch (error) {
          console.error("Map: Error fitting bounds:", error);
          map.setView([25.5941, 85.1376], 13);
        }
      } else {
        map.setView([25.5941, 85.1376], 13);
      }

      return () => {
        window.removeEventListener("resize", handleResize);
        map.off("baselayerchange");
        map.off("overlayadd");
        map.off("overlayremove");

        if (map._iconObserver) {
          map._iconObserver.disconnect();
          delete map._iconObserver;
        }

        map.removeControl(control);
        map.removeControl(geocoderControl);
        Object.values(overlayLayers).forEach((layer) => map.removeLayer(layer));
        Object.values(baseLayers).forEach((layer) => {
          if (map.hasLayer(layer)) {
            map.removeLayer(layer);
          }
        });
      };
    }, [
      filteredItem.data,
      filteredItem.dataSource,
      filteredItem.fieldsBySource,
      widgetIcons,
      uploadedIcons,
      iconSizes,
    ]);

    return null;
  };

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        minHeight: "400px",
      }}
    >
      <MapContainer
        center={filteredItem.center || [25.5941, 85.1376]}
        zoom={filteredItem.zoom || 13}
        attributionControl={false}
        maxZoom={18}
        style={{
          height: "100%",
          width: "100%",
          borderRadius: "8px",
          position: "absolute",
        }}
      >
        <MapMarkers />
      </MapContainer>

      <style>{`
        .map-legend-horizontal {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 6px 12px;
          border-radius: 6px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          z-index: 1000;
          display: flex;
          gap: 15px;
          align-items: center;
          font-size: 12px;
        }
        .map-legend-horizontal span {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .leaflet-control-layers-overlays label img {
          object-fit: contain !important;
          max-width: 18px !important;
          max-height: 18px !important;
        }
      `}</style>
    </div>
  );
};

export default Map;
